<template>
    <div v-if="isInit">
        <validation-observer ref="observer">
            <!-- 신청자 정보 -->
            <apply-step1 v-model="step1"></apply-step1>

            <!-- 방문 정보 -->
            <apply-step2 v-model="step2"></apply-step2>

            <!-- 방문자 목록 -->
            <apply-step3
                    v-model="step3.visitors"
                    :carry-in-items.sync="step4.carryInItems"
                    :agent="step1"
                    name="방문자"
            >
            </apply-step3>

            <!-- 반입물품 -->
            <apply-step4
                    v-if="isUseImportItem"
                    v-model="step4.carryInItems"
                    :visitors="step3.visitors"
            >
            </apply-step4>
        </validation-observer>
    </div>
</template>

<script>
import utilService from "@/service/utilService";
import {mapState} from "vuex";
import {ValidationObserver} from 'vee-validate';
import ApplyStep1 from "@/components/apply/ApplyStep1.vue";
import ApplyStep2 from "@/components/apply/ApplyStep2.vue";
import ApplyStep3 from "@/components/apply/ApplyStep3.vue";
import ApplyStep4 from "@/components/apply/ApplyStep4.vue";
import ApplyActionMixin from "@/components/apply/ApplyActionMixin";
import TermsMixin from "@/pages/home/views/apply/TermsMixin";

export default {
    name: 'ApplyEdit',
    mixins: [
        ApplyActionMixin,
        TermsMixin
    ],
    props: {
        visitId: Number
    },

    components: {
        ValidationObserver,
        ApplyStep1,
        ApplyStep2,
        ApplyStep3,
        ApplyStep4,
    },

    data: () => ({
        isInit: false,
        valid: false,
        isLoading: false,
        useAutoApprove: false,
        originVisitInfo: null,

        // new step1
        step1: {
            agentName: null,
            agentMobile: null,
            agentCompany: null
        },

        step2: {
            purpose: null,
            startDate: null,
            startTime: null,
            endDate: null,
            endTime: null,
            company: null,
            area: null,
            targetUser: null,
        },

        // step3
        step3: {
            visitors: [],
        },

        step4: {
            carryInItems: [],
        },
    }),

    computed: {
        ...mapState(['currentUser', 'secretKey', 'iv']),
        visitStatus() {
            return this.originVisitInfo ? this.originVisitInfo.visitStatus : null;
        },
        isUseImportItem() {
            return this.$code.get('service.config.use_import_item') === 'Y';
        },
    },

    async created() {
        if (this.visitId) {
            await this.fetchApplyInfo(this.visitId)
        } else {
            this.isInit = true;
        }
    },

    methods: {
        _transformUser(orgVisitors) {
            const visitors = [];

            orgVisitors.forEach(v => {
                visitors.push({
                    uuid: utilService.uuid(),
                    visitorName: v.visitorName,
                    visitorMobile: v.visitorMobile,
                    visitorCompany: v.visitorCompany,
                    visitorBirthday: v.visitorBirthday,
                    visitorCarNumber: v.visitorCarNumber,
                    vip: v.vip,
                    visitorId: v.visitorId,
                })
            })

            return visitors;
        },

        _initStore(visitInfo) {
            const startDatetime = visitInfo.startDatetime ? this.$moment(visitInfo.startDatetime) : null;

            this.step1 = {
                agentName: visitInfo.agentName,
                agentMobile: visitInfo.agentMobile,
                agentCompany: visitInfo.agentCompany
            }

            this.step2 = {
                purpose: visitInfo.purpose,
                startDate: startDatetime ? startDatetime.format('YYYY-MM-DD') : null,
                startTime: startDatetime ? startDatetime.format('HH:mm') : null,

                company: {
                    companyId: visitInfo.visitCompanyId,
                    companyName: visitInfo.visitCompanyName,
                },
                area: {
                    companyId: visitInfo.visitCompanyId,
                    companyName: visitInfo.visitCompanyName,
                    areaId: visitInfo.visitAreaId,
                    areaName: visitInfo.visitAreaName,
                },
                targetUser: {
                    userId: visitInfo.visitUserId,
                    userName: visitInfo.visitUserName,
                    titleName: visitInfo.visitTitleName,
                    mobile: visitInfo.visitUserEncryptedMobile,
                    encryptedMobile: visitInfo.visitUserEncryptedMobile,
                    deptId: visitInfo.visitDeptId,
                    deptName: visitInfo.visitDeptName
                }
            }
        },

        async fetchApplyInfo(visitId) {
            this.step3.visitors = [];
            this.step4.carryInItems = [];
            this.$window.showLoading("로딩 중...");

            try {
                let { visitInfo, visitors, carryInItems} = await this.getApplyInfo(visitId);
                this.originVisitInfo = visitInfo;
                visitors = this._transformUser(visitors);
                this.step3.visitors = [ ... visitors ];
                this.step4.carryInItems = carryInItems;
                this._initStore(this.originVisitInfo);
                this.isInit = true;
            } catch(err) {
                this.$window.alert(err.message, 'error');
            } finally {
                this.$window.hideLoading();
            }
        },

        async validate() {
            return await this.$refs.observer.validate();
        },

        async submit() {
            const success = await this.validate();

            if (!success) {
                this.$window.alert('신청 내용을 확인해주세요.', 'error');
                return;
            }

            const visitors = this.step3.visitors.map(visitor => {
                return {
                    ...visitor,
                    visitorMobile: utilService.normalizeMobile(visitor.visitorMobile),
                    termsChecked: this.termsAgreements.join(",")
                }
            })

            this.$window.showLoading("저장 중...");

            try {
                const accessTargetList = [];
                for (const visitor of visitors) {
                    const response = await this.$axios.get(`/v1/access/target/findByName/${encodeURIComponent(visitor.visitorName)}/${encodeURIComponent(visitor.visitorMobile)}`);
                    const target = response?.data || null;
                    if (target) {
                        accessTargetList.push(target);
                    }
                }

                const startDatetime = this.$moment(`${this.step2.startDate} ${this.step2.startTime}`);

                // 방문 신청 또는 변경
                const res = await this.$axios.put('/v1/home/visit/apply', {
                    visitId: this.visitId,
                    step1: {
                        name: this.step1.agentName,
                        mobile: utilService.normalizeMobile(this.step1.agentMobile),
                        company: this.step1.agentCompany,
                        purpose: this.step2.purpose, // TODO 리팩토링 필요
                    },
                    step2: {
                        startDatetime: startDatetime.toDate(),
                        company: this.step2.company,
                        area: this.step2.area,
                        user: this.step2.targetUser
                    },
                    step3: {
                        visitors: visitors,
                        importItems: this.step4.carryInItems
                    }
                });

                const visitInfo = res.data;
                const visitUserList = visitInfo?.visitUsers;
                for (const visitor of visitUserList) {
                    if (visitor.vip === 'Y') {
                        // VIP 승인 요청
                        await this.notificationApplication(visitInfo, visitor);
                        continue;
                    }
                    const accessTargetInfo = accessTargetList.find(item => item.visitorName === visitor.visitorName && item.visitorMobile === visitor.visitorMobile);
                    if (accessTargetInfo && accessTargetInfo !== "" && accessTargetInfo?.educationDateTime) {
                        const expiryDate = this.$moment(accessTargetInfo.expiryDate);
                        if (startDatetime.isSameOrBefore(expiryDate, 'day')) {
                            // 교육이수자 승인 요청
                            accessTargetInfo.visitorId = visitor.visitorId;
                            await this.$axios.put("/v1/access/target/update", accessTargetInfo);
                            await this.notificationApplication(visitInfo, visitor);
                            continue;
                        } else {
                            // 교육 재이수 요청
                            console.log("교육 재이수 요청!!!");
                            console.log("방문자 : ",visitor.visitorName);
                            console.log("방문일 : ",startDatetime);
                            console.log("만료일 : ",expiryDate);
                            await this.notificationReEducation(visitInfo, visitor);
                            continue;
                        }
                    }
                    // 교육 이수 요청
                    await this.notificationEducation(visitInfo, visitor);
                }

                if (this.visitId) {
                    this.$window.alert('변경 되었습니다.');
                    this.$emit("change");
                    this.$emit("update:show", false);
                } else {
                    this.$window.alert('신청이 완료 되었습니다.');
                    await this.$router.replace('/home');
                }

            } catch(err) {
                console.error(err);
                this.$window.alert(err.message ? err.message : err, 'error');
            } finally {
                this.$window.hideLoading();
            }
        },
    }

}
</script>

<style>
.error-message {
    font-size: 1.2rem !important;
    color: red;
    margin-left: 10px;
}

.boundary {
    height: 10px
}
</style>